import { ContextStorage } from "@/libs/contextStorage";

export const getConfigData = () => {
  const store = ContextStorage.store.getState();
  const { language, settings } = store.common;
  const { pageType } = store.page;

  const { countryCode, currencyCode, countryName, countryId } = settings;

  return {
    language,
    countryCode,
    currency: currencyCode,
    countryName,
    countryId,
    pageType
  };
};

class DynamicYieldEventService {
  addToCart({
    cartData,
    itemData
  }: {
    cartData: { products: { [key: string]: any }[] };
    itemData: { [key: string]: any };
  }) {
    const price = itemData?.productPrice || itemData?.price?.current || 0;
    const value = parseFloat((price * itemData.quantity).toFixed());
    const { currency } = getConfigData();

    const cart = cartData.products
      .map(item => ({
        productId: item.upc,
        quantity: item.quantity,
        itemPrice: item.price.current
      }))
      .reverse();

    this.dynamicYieldEvent({
      name: "Add to Cart",
      properties: {
        dyType: "add-to-cart-v1",
        value,
        currency,
        productId: itemData.upc,
        quantity: itemData.quantity,
        size: itemData.size,
        cart
      }
    });
  }

  removeFromCart({
    cartData,
    itemData
  }: {
    cartData: { products: { [key: string]: any }[] };
    itemData: { [key: string]: any };
  }) {
    const value = parseFloat(
      (itemData.price.current * itemData.quantity).toFixed()
    );
    const { currency } = getConfigData();
    const cart = cartData.products
      .map(item => ({
        productId: item.upc,
        quantity: item.quantity,
        itemPrice: item.price.current
      }))
      .reverse();

    this.dynamicYieldEvent({
      name: "Remove from Cart",
      properties: {
        dyType: "remove-from-cart-v1",
        value,
        currency,
        productId: itemData.upc,
        quantity: itemData.quantity,
        size: itemData.size,
        cart
      }
    });
  }

  purchase(data: any) {
    const { currency } = getConfigData();

    const cart =
      data?.items?.map(item => ({
        productId: item?.upc,
        quantity: item?.quantity,
        itemPrice: item?.price?.current,
        size: item?.size
      })) || [];

    this.dynamicYieldEvent({
      name: "Purchase",
      properties: {
        uniqueTransactionId: data?.orderId || "",
        dyType: "purchase-v1",
        value: data?.transactionAmount || 0,
        currency,
        cart
      }
    });
  }

  addToWishlist({ productId, size }: { productId: string; size: string }) {
    this.dynamicYieldEvent({
      name: "Add to Wishlist",
      properties: {
        dyType: "add-to-wishlist-v1",
        productId,
        ...(size && { size })
      }
    });
  }

  newsletterSubscription(email: string) {
    const hashedEmail = this.hashSha256(email);
    this.dynamicYieldEvent({
      name: "Newsletter Subscription",
      properties: {
        dyType: "newsletter-subscription-v1",
        hashedEmail
      }
    });
  }

  keywordSearch(keywords: string) {
    this.dynamicYieldEvent({
      name: "Keyword Search",
      properties: {
        dyType: "keyword-search-v1",
        keywords
      }
    });
  }

  sort(sort: { defaultValue: string; key: string }) {
    let sortData = {};
    if (sort.defaultValue.toLowerCase().includes("price")) {
      sortData = { sortBy: "PRICE", sortOrder: sort.key.toUpperCase() };
    } else {
      sortData = {
        sortBy: sort.defaultValue.toUpperCase(),
        sortOrder: sort.key.toUpperCase()
      };
    }

    this.dynamicYieldEvent({
      name: "Sort Items",
      properties: {
        dyType: "sort-items-v1",
        ...sortData
      }
    });
  }

  promoCode(code: string) {
    this.dynamicYieldEvent({
      name: "Promo Code Entered",
      properties: {
        dyType: "enter-promo-code-v1",
        code
      }
    });
  }

  login(email: string) {
    const hashedEmail = this.hashSha256(email);
    this.dynamicYieldEvent({
      name: "Login",
      properties: {
        dyType: "login-v1",
        hashedEmail
      }
    });
  }

  signUp(email: string) {
    const hashedEmail = this.hashSha256(email);
    this.dynamicYieldEvent({
      name: "Signup",
      properties: {
        dyType: "signup-v1",
        hashedEmail
      }
    });
  }

  private dynamicYieldEvent(data: {
    name: string;
    properties: { dyType: string; [key: string]: any };
  }) {
    const DY = window.DY || {};
    window.DY = DY;
    window.DY?.API?.("event", data);
  }

  private hashSha256(data: string) {
    const DYO = window.DYO || {};
    window.DYO = DYO;
    return window.DYO.dyhash?.sha256?.(data.toLowerCase());
  }
}

export default new DynamicYieldEventService();
